import { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import {
  ArrowLeft, Users, Crown, Settings, FileText, RefreshCw,
  BarChart3, Languages, Bell, Power, Heart, Activity,
  CheckCircle, XCircle, AlertCircle, Clock, Mail, Smartphone,
  Calendar, Database, Zap, Download, SlidersHorizontal, X,
  Search, ChevronLeft, ChevronRight, Check, Moon
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useI18n } from '../contexts/I18nContext';
import client, { getStoredToken } from '../utils/api';

// ── Mini bar chart (CSS only, no library) ──────────
const MiniBar = ({ data, color = 'bg-slate-800' }) => {
  const max = Math.max(...data.map(d => d.count), 1);
  return (
    <div className="flex items-end gap-1 h-16">
      {data.map((d, i) => (
        <div key={i} className="flex-1 flex flex-col items-center gap-1">
          <div className={`w-full rounded-t-md ${color} transition-all`}
            style={{ height: `${Math.max(4, (d.count / max) * 52)}px` }} />
          <span className="text-[9px] text-slate-400 leading-none">{d.date.split(' ')[1]}</span>
        </div>
      ))}
    </div>
  );
};

// ── Status pill ─────────────────────────────────────
const StatusPill = ({ ok, label }) => (
  <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold ${ok ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'}`}>
    {ok ? <CheckCircle size={11} /> : <XCircle size={11} />}{label}
  </span>
);

// ── Stat card ────────────────────────────────────────
const StatCard = ({ label, value, sub, color = 'bg-slate-100 text-slate-700', testId }) => (
  <div className="bg-white p-4 rounded-2xl shadow-[0_4px_20px_rgba(0,0,0,0.04)]" data-testid={testId}>
    <p className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>{value ?? '—'}</p>
    <p className={`text-xs font-semibold mt-1 inline-block px-2 py-0.5 rounded-full ${color}`}>{label}</p>
    {sub && <p className="text-xs text-slate-400 mt-1">{sub}</p>}
  </div>
);

// ── Calendar type badge ──────────────────────────────
const calColor = { gregorian: 'bg-blue-100 text-blue-700', hijri: 'bg-emerald-100 text-emerald-700', persian: 'bg-orange-100 text-orange-700' };
const statusColor = { sent: 'bg-emerald-100 text-emerald-700', pending: 'bg-amber-100 text-amber-700', failed: 'bg-red-100 text-red-700' };

const AdminPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { t } = useI18n();
  const [tab, setTab] = useState('stats');
  const [users, setUsers] = useState([]);
  const [config, setConfig] = useState({});
  const [logs, setLogs] = useState([]);
  const [stats, setStats] = useState({});
  const [translations, setTranslations] = useState({});
  const [notifications, setNotifications] = useState({});
  const [health, setHealth] = useState(null);
  const [notifStats, setNotifStats] = useState(null);
  const [userAnalytics, setUserAnalytics] = useState(null);
  const [reminderAnalytics, setReminderAnalytics] = useState(null);
  const [usersReport, setUsersReport] = useState([]);
  const [remindersReport, setRemindersReport] = useState([]);
  const [rptTotal, setRptTotal] = useState(0);
  const [rptLoading, setRptLoading] = useState(false);
  const [rptCreatedFrom, setRptCreatedFrom] = useState('');
  const [rptCreatedTo, setRptCreatedTo] = useState('');
  const [rptModifiedFrom, setRptModifiedFrom] = useState('');
  const [rptModifiedTo, setRptModifiedTo] = useState('');
  const [rptNextSchedFrom, setRptNextSchedFrom] = useState('');
  const [rptNextSchedTo, setRptNextSchedTo] = useState('');
  const [rptLastTrigFrom, setRptLastTrigFrom] = useState('');
  const [rptLastTrigTo, setRptLastTrigTo] = useState('');
  const [rptNeverTriggered, setRptNeverTriggered] = useState(false);
  const [rptCalType, setRptCalType] = useState('');
  const [queueReport, setQueueReport] = useState([]);

  // Users list pagination/filter
  const [usersTotal, setUsersTotal] = useState(0);
  const [usersPage, setUsersPage] = useState(0);
  const [userSearch, setUserSearch] = useState('');
  const [userRegFrom, setUserRegFrom] = useState('');
  const [userRegTo, setUserRegTo] = useState('');
  const [usersLoading, setUsersLoading] = useState(false);

  // Users report pagination/filter
  const [usrRptTotal, setUsrRptTotal] = useState(0);
  const [usrRptPage, setUsrRptPage] = useState(0);
  const [usrRptSearch, setUsrRptSearch] = useState('');
  const [usrRptRegFrom, setUsrRptRegFrom] = useState('');
  const [usrRptRegTo, setUsrRptRegTo] = useState('');
  const [usrRptLoading, setUsrRptLoading] = useState(false);

  // Admin action buttons
  const [seedLoading, setSeedLoading] = useState(false);
  const [seedResult, setSeedResult] = useState(null);
  const [testNotifLoading, setTestNotifLoading] = useState(false);
  const [testNotifResult, setTestNotifResult] = useState(null);
  const [forceResendLoading, setForceResendLoading] = useState(null); // reminder_id string while loading
  const [forceResendResult, setForceResendResult] = useState({});     // { [reminder_id]: { ok, message } }
  const [reportTab, setReportTab] = useState('users');
  const [editLang, setEditLang] = useState('en');
  const [editNotifLang, setEditNotifLang] = useState('en');
  const [transJsonText, setTransJsonText] = useState('');
  const [notifJsonText, setNotifJsonText] = useState('');
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [usersR, configR, logsR, statsR, transR, notifR, healthR, nStatsR, uAnalR, rAnalR, queueR, usersRep, remindersRep] = await Promise.all([
        client.get('/api/admin/users').then(r => r.data).catch(() => ({ data: [], total: 0 })),
        client.get('/api/admin/config').then(r => r.data).catch(() => ({})),
        client.get('/api/admin/logs').then(r => r.data).catch(() => []),
        client.get('/api/admin/stats').then(r => r.data).catch(() => ({})),
        client.get('/api/admin/translations').then(r => r.data).catch(() => ({})),
        client.get('/api/admin/notifications').then(r => r.data).catch(() => ({})),
        client.get('/api/admin/health').then(r => r.data).catch(() => null),
        client.get('/api/admin/notification-stats').then(r => r.data).catch(() => null),
        client.get('/api/admin/user-analytics').then(r => r.data).catch(() => null),
        client.get('/api/admin/reminder-analytics').then(r => r.data).catch(() => null),
        client.get('/api/admin/notification-queue-report').then(r => r.data).catch(() => []),
        client.get('/api/admin/users-report').then(r => r.data).catch(() => []),
        client.get('/api/admin/reminders-report').then(r => r.data).catch(() => ({ data: [], total: 0 })),
      ]);
      setUsers(Array.isArray(usersR) ? usersR : (usersR?.data || []));
      setUsersTotal(usersR?.total ?? (Array.isArray(usersR) ? usersR.length : 0));
      setConfig(configR || {}); setLogs(Array.isArray(logsR) ? logsR : []); setStats(statsR || {});
      setTranslations(transR); setNotifications(notifR);
      setTransJsonText(JSON.stringify(transR[editLang] || {}, null, 2));
      setNotifJsonText(JSON.stringify(notifR[editNotifLang] || {}, null, 2));
      setHealth(healthR); setNotifStats(nStatsR); setUserAnalytics(uAnalR);
      setReminderAnalytics(rAnalR); setQueueReport(Array.isArray(queueR) ? queueR : []);
      setUsersReport(Array.isArray(usersRep) ? usersRep : (usersRep?.data || []));
      setUsrRptTotal(usersRep?.total ?? (Array.isArray(usersRep) ? usersRep.length : 0));
      setRemindersReport(Array.isArray(remindersRep) ? remindersRep : (remindersRep?.data || []));
      setRptTotal(remindersRep?.total ?? (Array.isArray(remindersRep) ? remindersRep.length : 0));
    } catch (err) { console.error('Admin load:', err); }
    finally { setLoading(false); }
  }, [editLang, editNotifLang]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (user?.role !== 'admin') { navigate('/home'); return; }
    loadData();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, navigate]);

  useEffect(() => { setTransJsonText(JSON.stringify(translations[editLang] || {}, null, 2)); }, [editLang, translations]);
  useEffect(() => { setNotifJsonText(JSON.stringify(notifications[editNotifLang] || {}, null, 2)); }, [editNotifLang, notifications]);

  const loadRemindersReport = async (filters = {}) => {
    setRptLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.created_from)    params.set('created_from',    filters.created_from);
      if (filters.created_to)      params.set('created_to',      filters.created_to);
      if (filters.modified_from)   params.set('modified_from',   filters.modified_from);
      if (filters.modified_to)     params.set('modified_to',     filters.modified_to);
      if (filters.next_sched_from) params.set('next_sched_from', filters.next_sched_from);
      if (filters.next_sched_to)   params.set('next_sched_to',   filters.next_sched_to);
      if (filters.last_trig_from)  params.set('last_trig_from',  filters.last_trig_from);
      if (filters.last_trig_to)    params.set('last_trig_to',    filters.last_trig_to);
      if (filters.never_triggered) params.set('never_triggered', 'true');
      if (filters.calendar_type)   params.set('calendar_type',   filters.calendar_type);
      const res = await client.get(`/api/admin/reminders-report?${params}`).then(r => r.data);
      setRemindersReport(Array.isArray(res) ? res : (res?.data || []));
      setRptTotal(res?.total ?? (Array.isArray(res) ? res.length : 0));
    } catch (err) { console.error(err); }
    finally { setRptLoading(false); }
  };

  const applyRptFilters = () => loadRemindersReport({
    created_from: rptCreatedFrom, created_to: rptCreatedTo,
    modified_from: rptModifiedFrom, modified_to: rptModifiedTo,
    next_sched_from: rptNextSchedFrom, next_sched_to: rptNextSchedTo,
    last_trig_from: rptLastTrigFrom, last_trig_to: rptLastTrigTo,
    never_triggered: rptNeverTriggered,
    calendar_type: rptCalType,
  });

  const resetRptFilters = () => {
    setRptCreatedFrom(''); setRptCreatedTo('');
    setRptModifiedFrom(''); setRptModifiedTo('');
    setRptNextSchedFrom(''); setRptNextSchedTo('');
    setRptLastTrigFrom(''); setRptLastTrigTo('');
    setRptNeverTriggered(false);
    setRptCalType('');
    loadRemindersReport({});
  };

  const downloadRemindersCSV = async () => {
    const token = getStoredToken();
    const params = new URLSearchParams();
    if (rptCreatedFrom)    params.set('created_from',    rptCreatedFrom);
    if (rptCreatedTo)      params.set('created_to',      rptCreatedTo);
    if (rptModifiedFrom)   params.set('modified_from',   rptModifiedFrom);
    if (rptModifiedTo)     params.set('modified_to',     rptModifiedTo);
    if (rptNextSchedFrom)  params.set('next_sched_from', rptNextSchedFrom);
    if (rptNextSchedTo)    params.set('next_sched_to',   rptNextSchedTo);
    if (rptLastTrigFrom)   params.set('last_trig_from',  rptLastTrigFrom);
    if (rptLastTrigTo)     params.set('last_trig_to',    rptLastTrigTo);
    if (rptNeverTriggered) params.set('never_triggered', 'true');
    if (rptCalType)        params.set('calendar_type',   rptCalType);
    const base = process.env.REACT_APP_API_URL || process.env.REACT_APP_BACKEND_URL || '';
    const resp = await fetch(`${base}/api/admin/reminders-report/csv?${params}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!resp.ok) { alert('Download failed'); return; }
    const blob = await resp.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `reminders_${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const PAGE_SIZE = 100;

  const seedDemoReminders = async () => {
    setSeedLoading(true); setSeedResult(null);
    try {
      const res = await client.post('/api/admin/seed-demo-reminders').then(r => r.data);
      setSeedResult(`Created ${res.created}, skipped ${res.skipped} existing.`);
    } catch (e) { setSeedResult('Failed: ' + (e.response?.data?.detail || e.message)); }
    finally { setSeedLoading(false); }
  };

  const sendTestNotification = async () => {
    setTestNotifLoading(true); setTestNotifResult(null);
    try {
      const res = await client.post('/api/admin/send-test-notification').then(r => r.data);
      const pushOk = (res.push || []).filter(p => p.ok).length;
      setTestNotifResult(`Push: ${pushOk}/${res.push_subs_found} · Email: ${res.email}`);
    } catch (e) { setTestNotifResult('Failed: ' + (e.response?.data?.detail || e.message)); }
    finally { setTestNotifLoading(false); }
  };

  const handleForceResend = async (reminderId, reminderTitle) => {
    setForceResendLoading(reminderId);
    setForceResendResult(prev => ({ ...prev, [reminderId]: null }));
    try {
      const res = await client.post(`/api/admin/force-resend/${reminderId}`).then(r => r.data);
      setForceResendResult(prev => ({
        ...prev,
        [reminderId]: { ok: res.ok, message: res.message || res.error }
      }));
    } catch (e) {
      setForceResendResult(prev => ({
        ...prev,
        [reminderId]: { ok: false, message: e.response?.data?.detail || e.message }
      }));
    } finally {
      setForceResendLoading(null);
    }
  };


  const loadUsers = async (filters = {}, page = 0) => {
    setUsersLoading(true);
    try {
      const params = new URLSearchParams({ skip: page * PAGE_SIZE, limit: PAGE_SIZE });
      if (filters.search) params.set('search', filters.search);
      if (filters.reg_from) params.set('reg_from', filters.reg_from);
      if (filters.reg_to) params.set('reg_to', filters.reg_to);
      const res = await client.get(`/api/admin/users?${params}`).then(r => r.data);
      setUsers(Array.isArray(res) ? res : (res?.data || []));
      setUsersTotal(res?.total ?? (Array.isArray(res) ? res.length : 0));
      setUsersPage(page);
    } catch (err) { console.error(err); }
    finally { setUsersLoading(false); }
  };

  const loadUsersReport = async (filters = {}, page = 0) => {
    setUsrRptLoading(true);
    try {
      const params = new URLSearchParams({ skip: page * PAGE_SIZE, limit: PAGE_SIZE });
      if (filters.search) params.set('search', filters.search);
      if (filters.reg_from) params.set('reg_from', filters.reg_from);
      if (filters.reg_to) params.set('reg_to', filters.reg_to);
      const res = await client.get(`/api/admin/users-report?${params}`).then(r => r.data);
      setUsersReport(Array.isArray(res) ? res : (res?.data || []));
      setUsrRptTotal(res?.total ?? (Array.isArray(res) ? res.length : 0));
      setUsrRptPage(page);
    } catch (err) { console.error(err); }
    finally { setUsrRptLoading(false); }
  };

  const togglePremium = async (userId) => {
    try {
      const res = await client.put(`/api/admin/users/${userId}/toggle-premium`).then(r => r.data);
      setUsers(prev => prev.map(u => u.id === userId ? { ...u, subscription: res.subscription } : u));
    } catch (err) { console.error(err); }
  };

  const toggleActive = async (userId) => {
    try {
      const res = await client.put(`/api/admin/users/${userId}/toggle-active`).then(r => r.data);
      setUsers(prev => prev.map(u => u.id === userId ? { ...u, active: res.active } : u));
    } catch (err) { console.error(err); }
  };

  const toggleAdmin = async (userId, currentRole) => {
    const action = currentRole === 'admin' ? 'Remove admin role from' : 'Promote to admin';
    if (!window.confirm(`${action} this user?`)) return;
    try {
      const res = await client.put(`/api/admin/users/${userId}/toggle-admin`).then(r => r.data);
      setUsers(prev => prev.map(u => u.id === userId ? { ...u, role: res.role } : u));
    } catch (err) {
      alert(err?.response?.data?.detail || 'Failed to update role');
    }
  };

  const updateConfig = async (key, value) => {
    const nc = { ...config, [key]: value }; setConfig(nc);
    try { await client.put('/api/admin/config', nc); } catch (err) { console.error(err); }
  };

  const saveTranslations = async () => {
    setSaving(true);
    try {
      const parsed = JSON.parse(transJsonText);
      const updated = { ...translations, [editLang]: parsed };
      await client.put('/api/admin/translations', updated);
      setTranslations(updated);
    } catch (err) { alert('Invalid JSON: ' + err.message); }
    finally { setSaving(false); }
  };

  const saveNotifications = async () => {
    setSaving(true);
    try {
      const parsed = JSON.parse(notifJsonText);
      const updated = { ...notifications, [editNotifLang]: parsed };
      await client.put('/api/admin/notifications', updated);
      setNotifications(updated);
    } catch (err) { alert('Invalid JSON: ' + err.message); }
    finally { setSaving(false); }
  };

  const langs = ['en', 'ar', 'fa', 'tr', 'id', 'ms'];

  const pct = (n, total) => total > 0 ? Math.round((n / total) * 100) : 0;

  if (loading) {
    return (
      <div className="w-full max-w-md mx-auto min-h-screen bg-[#F7F9FB] flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-2 border-slate-900 border-t-transparent rounded-full" />
      </div>
    );
  }

  const tabs = [
    { id: 'stats',   icon: BarChart3,  label: t('admin.stats') },
    { id: 'health',  icon: Activity,   label: 'Health' },
    { id: 'reports', icon: FileText,   label: 'Reports' },
    { id: 'users',   icon: Users,      label: t('admin.users') },
    { id: 'config',  icon: Settings,   label: t('admin.config') },
    { id: 'trans',   icon: Languages,  label: t('admin.i18n') },
    { id: 'notif',   icon: Bell,       label: t('admin.notif') },
    { id: 'logs',    icon: FileText,   label: t('admin.logs') },
  ];

  return (
    <div className="w-full max-w-md mx-auto min-h-screen bg-[#F7F9FB] relative flex flex-col">
      <div className="p-6 pb-8 flex-1">
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
          <div className="flex items-center gap-4 mb-6">
            <button onClick={() => navigate('/settings')} data-testid="admin-back-button"
              className="w-10 h-10 bg-white rounded-full flex items-center justify-center shadow-sm active:scale-95 transition-transform">
              <ArrowLeft size={20} className="text-slate-900" />
            </button>
            <div className="flex-1">
              <h1 className="text-2xl font-bold tracking-tight text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>{t('admin.title')}</h1>
              {health && (
                <p className="text-xs text-slate-400 mt-0.5">
                  v{health.version} · started {health.server_start} · up {health.uptime}
                </p>
              )}
            </div>
            <button onClick={loadData} data-testid="admin-refresh" className="w-10 h-10 bg-white rounded-full flex items-center justify-center shadow-sm">
              <RefreshCw size={18} className="text-slate-600" />
            </button>
          </div>

          {/* Tabs - scrollable */}
          <div className="bg-slate-100 p-1 rounded-2xl flex mb-6 overflow-x-auto gap-1">
            {tabs.map(tb => (
              <button key={tb.id} onClick={() => setTab(tb.id)} data-testid={`admin-tab-${tb.id}`}
                className={`flex-shrink-0 px-3 py-2 text-center text-xs font-semibold rounded-xl transition-all flex items-center justify-center gap-1 ${
                  tab === tb.id ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500'
                }`}>
                <tb.icon size={12} />{tb.label}
              </button>
            ))}
          </div>

          {/* ── STATS TAB ──────────────────────────── */}
          {tab === 'stats' && (
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                {[
                  { label: t('admin.total_users'), value: stats.users || 0, color: 'bg-blue-100 text-blue-700', testId: 'stat-total-users' },
                  { label: t('admin.premium_users'), value: stats.premiumUsers || 0, color: 'bg-amber-100 text-amber-700', testId: 'stat-premium-users' },
                  { label: t('admin.reminders'), value: stats.reminders || 0, color: 'bg-emerald-100 text-emerald-700', testId: 'stat-reminders' },
                  { label: t('admin.groups'), value: stats.groups || 0, color: 'bg-purple-100 text-purple-700', testId: 'stat-groups' },
                  { label: t('admin.feedback'), value: stats.feedback || 0, color: 'bg-cyan-100 text-cyan-700', testId: 'stat-feedback' },
                ].map((s, i) => (
                  <StatCard key={i} {...s} />
                ))}
              </div>
            </div>
          )}

          {/* ── HEALTH TAB ─────────────────────────── */}
          {tab === 'health' && (
            <div className="space-y-4" data-testid="health-tab">

              {/* System Status */}
              <div className="bg-white p-4 rounded-2xl shadow-[0_4px_20px_rgba(0,0,0,0.04)]">
                <p className="text-sm font-bold text-slate-900 mb-1 flex items-center gap-2"><Database size={14} />System Status</p>
                {health?.version && (
                  <p className="text-xs text-slate-400 mb-3">
                    v{health.version} &nbsp;·&nbsp; Started: {health.server_start} &nbsp;·&nbsp; Uptime: {health.uptime}
                  </p>
                )}
                <div className="flex flex-wrap gap-2 mb-4">
                  <StatusPill ok={health?.mongodb?.ok} label="MongoDB" />
                  <StatusPill ok={health?.firebase?.ok} label="Firebase FCM" />
                  <StatusPill ok={!!health?.scheduler?.last_run} label="Scheduler" />
                  <StatusPill ok={!!health?.sender?.last_run} label="Sender" />
                </div>
                {health?.firebase?.error && (
                  <div className="bg-red-50 p-3 rounded-xl">
                    <p className="text-xs text-red-700 font-medium flex items-center gap-1"><AlertCircle size={11} />Firebase Error</p>
                    <p className="text-xs text-red-600 mt-1 break-all">{health.firebase.error}</p>
                  </div>
                )}
              </div>

              {/* Schedulers */}
              <div className="bg-white p-4 rounded-2xl shadow-[0_4px_20px_rgba(0,0,0,0.04)]">
                <p className="text-sm font-bold text-slate-900 mb-3 flex items-center gap-2"><Zap size={14} />Background Jobs</p>
                <div className="space-y-3">
                  {[
                    { label: 'Queue Scheduler', data: health?.scheduler, icon: Calendar },
                    { label: 'Notification Sender', data: health?.sender, icon: Bell },
                  ].map(({ label, data, icon: Icon }) => (
                    <div key={label} className="flex items-center justify-between p-3 bg-slate-50 rounded-xl">
                      <div className="flex items-center gap-2">
                        <div className="w-7 h-7 bg-slate-200 rounded-full flex items-center justify-center">
                          <Icon size={12} className="text-slate-600" />
                        </div>
                        <div>
                          <p className="text-xs font-semibold text-slate-900">{label}</p>
                          <p className="text-xs text-slate-400">{data?.interval || '—'}</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-xs font-semibold text-slate-700 flex items-center gap-1">
                          <Clock size={10} />{data?.last_run_ago || 'Not run yet'}
                        </p>
                        <p className="text-xs text-slate-400">{data?.run_count || 0} runs</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Notification Queue */}
              <div className="bg-white p-4 rounded-2xl shadow-[0_4px_20px_rgba(0,0,0,0.04)]">
                <p className="text-sm font-bold text-slate-900 mb-3 flex items-center gap-2"><Bell size={14} />Queue Snapshot</p>
                <div className="grid grid-cols-2 gap-2">
                  {[
                    { label: 'Pending', value: health?.queue?.pending ?? '—', color: 'bg-amber-100 text-amber-700' },
                    { label: 'Sent Today', value: health?.queue?.sent_today ?? '—', color: 'bg-emerald-100 text-emerald-700' },
                    { label: 'Failed Today', value: health?.queue?.failed_today ?? '—', color: 'bg-red-100 text-red-700' },
                    { label: 'Sent This Week', value: health?.queue?.sent_week ?? '—', color: 'bg-blue-100 text-blue-700' },
                  ].map((item, i) => (
                    <div key={i} className="p-3 bg-slate-50 rounded-xl">
                      <p className="text-xl font-bold text-slate-900">{item.value}</p>
                      <p className={`text-xs font-semibold mt-0.5 inline-block px-2 py-0.5 rounded-full ${item.color}`}>{item.label}</p>
                    </div>
                  ))}
                </div>
              </div>

              {/* Notification Stats */}
              {notifStats && (
                <div className="bg-white p-4 rounded-2xl shadow-[0_4px_20px_rgba(0,0,0,0.04)]">
                  <p className="text-sm font-bold text-slate-900 mb-3 flex items-center gap-2"><Activity size={14} />Notification Stats</p>
                  <div className="space-y-3">
                    {[
                      { label: 'Email', icon: Mail, today: notifStats.email?.today, week: notifStats.email?.week, color: 'text-blue-600' },
                      { label: 'Push', icon: Smartphone, today: notifStats.push?.today, week: notifStats.push?.week, color: 'text-purple-600' },
                    ].map(({ label, icon: Icon, today, week, color }) => (
                      <div key={label} className="flex items-center justify-between p-3 bg-slate-50 rounded-xl">
                        <div className="flex items-center gap-2">
                          <Icon size={14} className={color} />
                          <p className="text-sm font-semibold text-slate-900">{label}</p>
                        </div>
                        <div className="flex gap-4 text-right">
                          <div><p className="text-sm font-bold text-slate-900">{today ?? 0}</p><p className="text-xs text-slate-400">Today</p></div>
                          <div><p className="text-sm font-bold text-slate-900">{week ?? 0}</p><p className="text-xs text-slate-400">This week</p></div>
                          <div><p className="text-sm font-bold text-slate-900">{notifStats.total?.all_time ?? 0}</p><p className="text-xs text-slate-400">All time</p></div>
                        </div>
                      </div>
                    ))}
                    <div className="flex items-center justify-between p-3 bg-red-50 rounded-xl">
                      <div className="flex items-center gap-2">
                        <XCircle size={14} className="text-red-500" />
                        <p className="text-sm font-semibold text-slate-900">Failed</p>
                      </div>
                      <div className="flex gap-4 text-right">
                        <div><p className="text-sm font-bold text-red-700">{notifStats.failed?.today ?? 0}</p><p className="text-xs text-slate-400">Today</p></div>
                        <div><p className="text-sm font-bold text-red-700">{notifStats.failed?.all_time ?? 0}</p><p className="text-xs text-slate-400">All time</p></div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* ── Admin Actions ── */}
              <div className="bg-white rounded-2xl p-4 shadow-[0_2px_12px_rgba(0,0,0,0.05)]">
                <p className="text-sm font-bold text-slate-900 mb-3 flex items-center gap-2">
                  <Zap size={14} /> Admin Actions
                </p>
                <div className="space-y-3">
                  {/* Seed Demo Reminders */}
                  <div>
                    <button
                      onClick={seedDemoReminders}
                      disabled={seedLoading}
                      data-testid="seed-demo-btn"
                      className="w-full py-2.5 bg-slate-900 text-white text-xs font-semibold rounded-xl hover:bg-slate-700 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                    >
                      <Database size={13} />
                      {seedLoading ? 'Seeding…' : 'Seed Demo Reminders (9 reminders for this account)'}
                    </button>
                    {seedResult && <p className="text-xs text-slate-500 mt-1 text-center">{seedResult}</p>}
                  </div>
                  {/* Send Test Notification */}
                  <div>
                    <button
                      onClick={sendTestNotification}
                      disabled={testNotifLoading}
                      data-testid="test-notif-btn"
                      className="w-full py-2.5 bg-blue-600 text-white text-xs font-semibold rounded-xl hover:bg-blue-700 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                    >
                      <Bell size={13} />
                      {testNotifLoading ? 'Sending…' : 'Send Test Notification Now (Push + Email)'}
                    </button>
                    {testNotifResult && <p className="text-xs text-slate-500 mt-1 text-center">{testNotifResult}</p>}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* ── REPORTS TAB ────────────────────────── */}
          {tab === 'reports' && (
            <div className="space-y-4" data-testid="reports-tab">

              {/* Report sub-tabs */}
              <div className="bg-slate-100 p-1 rounded-2xl flex gap-1">
                {[
                  { id: 'users',     label: 'Users' },
                  { id: 'reminders', label: 'Reminders' },
                  { id: 'summary',   label: 'Summary' },
                  { id: 'queue',     label: 'Queue' },
                ].map(rt => (
                  <button key={rt.id} onClick={() => setReportTab(rt.id)}
                    className={`flex-1 py-2 text-xs font-semibold rounded-xl transition-all ${reportTab === rt.id ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500'}`}>
                    {rt.label}
                  </button>
                ))}
              </div>

              {/* ── Users Detail Report ── */}
              {reportTab === 'users' && (
                <div>
                  {/* Search + Filter Panel */}
                  <div className="bg-white rounded-2xl p-4 shadow-[0_2px_12px_rgba(0,0,0,0.05)] mb-4">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <SlidersHorizontal size={14} className="text-slate-500" />
                        <span className="text-xs font-semibold text-slate-700">Filters</span>
                      </div>
                    </div>
                    {/* Email search */}
                    <div className="relative mb-3">
                      <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                      <input
                        type="text"
                        value={usrRptSearch}
                        onChange={e => setUsrRptSearch(e.target.value)}
                        onKeyDown={e => e.key === 'Enter' && loadUsersReport({ search: usrRptSearch, reg_from: usrRptRegFrom, reg_to: usrRptRegTo }, 0)}
                        placeholder="Search by email…"
                        data-testid="usr-rpt-search"
                        className="w-full pl-8 pr-3 py-2 text-sm border border-slate-200 rounded-xl bg-slate-50 focus:outline-none focus:ring-2 focus:ring-slate-300"
                      />
                    </div>
                    {/* Reg date filters */}
                    <div className="mb-3">
                      <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wide mb-1">Registration Date</p>
                      <div className="grid grid-cols-2 gap-2">
                        <div>
                          <label className="text-[10px] text-slate-400">From</label>
                          <input type="date" value={usrRptRegFrom} onChange={e => setUsrRptRegFrom(e.target.value)}
                            data-testid="usr-rpt-reg-from"
                            className="w-full mt-0.5 px-2 py-1.5 text-xs border border-slate-200 rounded-xl bg-slate-50 focus:outline-none focus:ring-2 focus:ring-slate-300" />
                        </div>
                        <div>
                          <label className="text-[10px] text-slate-400">To</label>
                          <input type="date" value={usrRptRegTo} onChange={e => setUsrRptRegTo(e.target.value)}
                            data-testid="usr-rpt-reg-to"
                            className="w-full mt-0.5 px-2 py-1.5 text-xs border border-slate-200 rounded-xl bg-slate-50 focus:outline-none focus:ring-2 focus:ring-slate-300" />
                        </div>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <button onClick={() => loadUsersReport({ search: usrRptSearch, reg_from: usrRptRegFrom, reg_to: usrRptRegTo }, 0)}
                        data-testid="usr-rpt-apply-btn" disabled={usrRptLoading}
                        className="flex-1 py-2 bg-slate-900 text-white text-xs font-semibold rounded-xl hover:bg-slate-700 transition-colors disabled:opacity-50">
                        {usrRptLoading ? 'Loading…' : 'Apply Filters'}
                      </button>
                      <button onClick={() => { setUsrRptSearch(''); setUsrRptRegFrom(''); setUsrRptRegTo(''); loadUsersReport({}, 0); }}
                        data-testid="usr-rpt-reset-btn"
                        className="px-4 py-2 border border-slate-200 text-slate-600 text-xs font-semibold rounded-xl hover:bg-slate-50 transition-colors flex items-center gap-1">
                        <X size={12} /> Reset
                      </button>
                    </div>
                  </div>

                  {/* Count + pagination */}
                  <div className="flex items-center justify-between mb-3">
                    <p className="text-xs text-slate-400">
                      Showing <span className="font-semibold text-slate-700">{usersReport.length}</span> of{' '}
                      <span className="font-semibold text-slate-700">{usrRptTotal || usersReport.length}</span> users
                      {(usrRptTotal || usersReport.length) > 100 && <span className="text-amber-600"> (max 100 per page)</span>}
                    </p>
                    {(usrRptTotal > PAGE_SIZE) && (
                      <div className="flex items-center gap-2">
                        <button onClick={() => loadUsersReport({ search: usrRptSearch, reg_from: usrRptRegFrom, reg_to: usrRptRegTo }, usrRptPage - 1)}
                          disabled={usrRptPage === 0 || usrRptLoading}
                          className="p-1.5 border border-slate-200 rounded-xl disabled:opacity-40 hover:bg-slate-50 transition-colors">
                          <ChevronLeft size={14} />
                        </button>
                        <span className="text-xs text-slate-500">{usrRptPage + 1}/{Math.ceil(usrRptTotal / PAGE_SIZE)}</span>
                        <button onClick={() => loadUsersReport({ search: usrRptSearch, reg_from: usrRptRegFrom, reg_to: usrRptRegTo }, usrRptPage + 1)}
                          disabled={(usrRptPage + 1) * PAGE_SIZE >= usrRptTotal || usrRptLoading}
                          className="p-1.5 border border-slate-200 rounded-xl disabled:opacity-40 hover:bg-slate-50 transition-colors">
                          <ChevronRight size={14} />
                        </button>
                      </div>
                    )}
                  </div>

                  <div className="space-y-3">
                    {usersReport.map((u, i) => (
                      <div key={i} className="bg-white rounded-2xl p-4 shadow-[0_2px_12px_rgba(0,0,0,0.05)]" data-testid="users-report-row">
                        {/* Header */}
                        <div className="flex items-start justify-between mb-3">
                          <div>
                            <p className="text-sm font-bold text-slate-900">{u.name || '—'}</p>
                            <p className="text-xs text-slate-500 break-all">{u.email}</p>
                          </div>
                          <div className="flex gap-1 flex-wrap justify-end">
                            {u.role === 'admin' && <span className="px-2 py-0.5 bg-amber-100 text-amber-700 text-xs font-semibold rounded-full">Admin</span>}
                            <span className={`px-2 py-0.5 text-xs font-semibold rounded-full ${u.plan === 'premium' ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-600'}`}>{u.plan}</span>
                            <span className="px-2 py-0.5 bg-blue-50 text-blue-600 text-xs rounded-full">{u.provider}</span>
                          </div>
                        </div>
                        {/* Dates */}
                        <div className="grid grid-cols-2 gap-2 mb-3">
                          <div className="bg-slate-50 p-2 rounded-xl">
                            <p className="text-xs text-slate-400">Registered</p>
                            <p className="text-xs font-semibold text-slate-800">{u.registered || '—'}</p>
                            <p className="text-xs text-slate-400">{u.registered_ago}</p>
                          </div>
                          <div className="bg-slate-50 p-2 rounded-xl">
                            <p className="text-xs text-slate-400">Last Login</p>
                            <p className="text-xs font-semibold text-slate-800">{u.last_login || '—'}</p>
                            <p className="text-xs text-slate-400">{u.last_login_ago}</p>
                          </div>
                        </div>
                        {/* Notification channels */}
                        <div className="flex gap-2 flex-wrap mb-2">
                          <span className={`flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${u.notifications_enabled ? 'bg-emerald-50 text-emerald-700' : 'bg-slate-100 text-slate-400'}`}>
                            <Bell size={10} /> Notif {u.notifications_enabled ? 'On' : 'Off'}
                          </span>
                          <span className={`flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${u.email_notifications ? 'bg-blue-50 text-blue-700' : 'bg-slate-100 text-slate-400'}`}>
                            <Mail size={10} /> Email {u.email_notifications ? 'On' : 'Off'}
                          </span>
                          <span className={`flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${u.push_devices > 0 ? 'bg-purple-50 text-purple-700' : 'bg-slate-100 text-slate-400'}`}>
                            <Smartphone size={10} /> Push {u.push_devices > 0 ? `${u.push_devices} device${u.push_devices > 1 ? 's' : ''}` : 'None'}
                          </span>
                        </div>
                        {/* Stats row */}
                        <div className="flex gap-3 text-xs text-slate-500">
                          <span><span className="font-semibold text-slate-800">{u.reminder_count}</span> reminders</span>
                          <span>Lang: <span className="font-semibold text-slate-800">{u.language || 'en'}</span></span>
                          {u.hijri_adjustment !== 0 && u.hijri_adjustment != null && (
                            <span className="flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-emerald-50 text-emerald-700">
                              <Moon size={10} /> Hijri {u.hijri_adjustment > 0 ? `+${u.hijri_adjustment}` : u.hijri_adjustment}
                            </span>
                          )}
                          <span>TZ: <span className="font-semibold text-slate-800">{u.timezone || 'auto'}</span></span>
                        </div>
                        {u.last_notif_sent && (
                          <p className="text-xs text-slate-400 mt-1">Last notif: {u.last_notif_sent} via {u.last_notif_channel || '—'}</p>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* ── Reminders Detail Report ── */}
              {reportTab === 'reminders' && (
                <div>
                  {/* Filter Panel */}
                  <div className="bg-white rounded-2xl p-4 shadow-[0_2px_12px_rgba(0,0,0,0.05)] mb-4">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <SlidersHorizontal size={14} className="text-slate-500" />
                        <span className="text-xs font-semibold text-slate-700">Filters</span>
                      </div>
                      <button
                        onClick={downloadRemindersCSV}
                        data-testid="download-csv-btn"
                        className="flex items-center gap-1.5 px-3 py-1.5 bg-slate-900 text-white text-xs font-semibold rounded-xl hover:bg-slate-700 transition-colors"
                      >
                        <Download size={12} /> Download CSV
                      </button>
                    </div>
                    {/* Created Date Row */}
                    <div className="mb-2">
                      <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wide mb-1">Created Date</p>
                      <div className="grid grid-cols-2 gap-2">
                        <div>
                          <label className="text-[10px] text-slate-400">From</label>
                          <input type="date" value={rptCreatedFrom} onChange={e => setRptCreatedFrom(e.target.value)}
                            data-testid="filter-created-from"
                            className="w-full mt-0.5 px-2 py-1.5 text-xs border border-slate-200 rounded-xl bg-slate-50 focus:outline-none focus:ring-2 focus:ring-slate-300" />
                        </div>
                        <div>
                          <label className="text-[10px] text-slate-400">To</label>
                          <input type="date" value={rptCreatedTo} onChange={e => setRptCreatedTo(e.target.value)}
                            data-testid="filter-created-to"
                            className="w-full mt-0.5 px-2 py-1.5 text-xs border border-slate-200 rounded-xl bg-slate-50 focus:outline-none focus:ring-2 focus:ring-slate-300" />
                        </div>
                      </div>
                    </div>
                    {/* Modified Date Row */}
                    <div className="mb-3">
                      <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wide mb-1">Modified Date</p>
                      <div className="grid grid-cols-2 gap-2">
                        <div>
                          <label className="text-[10px] text-slate-400">From</label>
                          <input type="date" value={rptModifiedFrom} onChange={e => setRptModifiedFrom(e.target.value)}
                            data-testid="filter-modified-from"
                            className="w-full mt-0.5 px-2 py-1.5 text-xs border border-slate-200 rounded-xl bg-slate-50 focus:outline-none focus:ring-2 focus:ring-slate-300" />
                        </div>
                        <div>
                          <label className="text-[10px] text-slate-400">To</label>
                          <input type="date" value={rptModifiedTo} onChange={e => setRptModifiedTo(e.target.value)}
                            data-testid="filter-modified-to"
                            className="w-full mt-0.5 px-2 py-1.5 text-xs border border-slate-200 rounded-xl bg-slate-50 focus:outline-none focus:ring-2 focus:ring-slate-300" />
                        </div>
                      </div>
                    </div>
                    {/* Next Scheduled Date Row */}
                    <div className="mb-3">
                      <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wide mb-1">Next Scheduled</p>
                      <div className="grid grid-cols-2 gap-2">
                        <div>
                          <label className="text-[10px] text-slate-400">From</label>
                          <input type="date" value={rptNextSchedFrom} onChange={e => setRptNextSchedFrom(e.target.value)}
                            data-testid="filter-next-sched-from"
                            className="w-full mt-0.5 px-2 py-1.5 text-xs border border-slate-200 rounded-xl bg-slate-50 focus:outline-none focus:ring-2 focus:ring-slate-300" />
                        </div>
                        <div>
                          <label className="text-[10px] text-slate-400">To</label>
                          <input type="date" value={rptNextSchedTo} onChange={e => setRptNextSchedTo(e.target.value)}
                            data-testid="filter-next-sched-to"
                            className="w-full mt-0.5 px-2 py-1.5 text-xs border border-slate-200 rounded-xl bg-slate-50 focus:outline-none focus:ring-2 focus:ring-slate-300" />
                        </div>
                      </div>
                    </div>
                    {/* Last Triggered Date Row */}
                    <div className="mb-3">
                      <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wide mb-1">Last Triggered</p>
                      <div className="grid grid-cols-2 gap-2">
                        <div>
                          <label className="text-[10px] text-slate-400">From</label>
                          <input type="date" value={rptLastTrigFrom} onChange={e => setRptLastTrigFrom(e.target.value)}
                            data-testid="filter-last-trig-from"
                            className="w-full mt-0.5 px-2 py-1.5 text-xs border border-slate-200 rounded-xl bg-slate-50 focus:outline-none focus:ring-2 focus:ring-slate-300" />
                        </div>
                        <div>
                          <label className="text-[10px] text-slate-400">To</label>
                          <input type="date" value={rptLastTrigTo} onChange={e => setRptLastTrigTo(e.target.value)}
                            data-testid="filter-last-trig-to"
                            className="w-full mt-0.5 px-2 py-1.5 text-xs border border-slate-200 rounded-xl bg-slate-50 focus:outline-none focus:ring-2 focus:ring-slate-300" />
                        </div>
                      </div>
                    </div>
                    {/* Never Triggered Checkbox */}
                    <div className="mb-3">
                      <label className="flex items-center gap-2 cursor-pointer select-none group" data-testid="filter-never-triggered">
                        <div
                          onClick={() => setRptNeverTriggered(v => !v)}
                          className={`w-4 h-4 rounded border-2 flex items-center justify-center transition-colors ${rptNeverTriggered ? 'bg-slate-900 border-slate-900' : 'border-slate-300 bg-white group-hover:border-slate-400'}`}
                        >
                          {rptNeverTriggered && <Check size={10} className="text-white" />}
                        </div>
                        <span className="text-xs font-medium text-slate-700">Show only "Never Triggered"</span>
                      </label>
                    </div>
                    {/* Calendar Type Filter */}
                    <div className="mb-4">
                      <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wide mb-1">Calendar Type</p>
                      <div className="flex gap-2">
                        {[
                          { value: '', label: 'All' },
                          { value: 'gregorian', label: 'Gregorian' },
                          { value: 'hijri', label: 'Hijri' },
                          { value: 'persian', label: 'Shamsi' },
                        ].map(opt => (
                          <button
                            key={opt.value}
                            data-testid={`filter-cal-type-${opt.value || 'all'}`}
                            onClick={() => setRptCalType(opt.value)}
                            className={`flex-1 py-1.5 rounded-xl text-xs font-semibold border transition-all ${
                              rptCalType === opt.value
                                ? opt.value === 'gregorian' ? 'bg-blue-600 text-white border-blue-600'
                                  : opt.value === 'hijri' ? 'bg-emerald-600 text-white border-emerald-600'
                                  : opt.value === 'persian' ? 'bg-orange-500 text-white border-orange-500'
                                  : 'bg-slate-900 text-white border-slate-900'
                                : 'border-slate-200 text-slate-600 hover:bg-slate-50'
                            }`}
                          >
                            {opt.label}
                          </button>
                        ))}
                      </div>
                    </div>
                    {/* Action buttons */}
                    <div className="flex gap-2">
                      <button onClick={applyRptFilters} data-testid="apply-filters-btn" disabled={rptLoading}
                        className="flex-1 py-2 bg-slate-900 text-white text-xs font-semibold rounded-xl hover:bg-slate-700 transition-colors disabled:opacity-50">
                        {rptLoading ? 'Loading…' : 'Apply Filters'}
                      </button>
                      <button onClick={resetRptFilters} data-testid="reset-filters-btn"
                        className="px-4 py-2 border border-slate-200 text-slate-600 text-xs font-semibold rounded-xl hover:bg-slate-50 transition-colors flex items-center gap-1">
                        <X size={12} /> Reset
                      </button>
                    </div>
                  </div>

                  {/* Count row */}
                  <p className="text-xs text-slate-400 mb-3">
                    Showing <span className="font-semibold text-slate-700">{remindersReport.length}</span> of{' '}
                    <span className="font-semibold text-slate-700">{rptTotal}</span> reminders
                    {rptTotal > 100 && <span className="text-amber-600"> (max 100 preview — download CSV for full data)</span>}
                  </p>

                  <div className="space-y-3">
                    {remindersReport.map((r, i) => (
                      <div key={i} className="bg-white rounded-2xl p-4 shadow-[0_2px_12px_rgba(0,0,0,0.05)]" data-testid="reminders-report-row">
                        {/* Header */}
                        <div className="flex items-start justify-between mb-2">
                          <p className="text-sm font-bold text-slate-900 flex-1">{r.title}</p>
                          <div className="flex gap-1 ml-2">
                            <span className={`px-2 py-0.5 text-xs font-semibold rounded-full ${calColor[r.calendar_type] || 'bg-slate-100 text-slate-600'}`}>{r.calendar_type}</span>
                            <span className="px-2 py-0.5 text-xs rounded-full bg-pink-50 text-pink-700">{r.category}</span>
                          </div>
                        </div>
                        <p className="text-xs text-slate-500 mb-3">By: {r.owner_name} ({r.owner_email}){r.owner_hijri_adjustment !== 0 && r.owner_hijri_adjustment != null && (
                          <span className="ml-2 px-1.5 py-0.5 rounded-md text-[10px] font-bold bg-emerald-50 text-emerald-700">Hijri {r.owner_hijri_adjustment > 0 ? `+${r.owner_hijri_adjustment}` : r.owner_hijri_adjustment}</span>
                        )}</p>
                        {/* Date info */}
                        <div className="grid grid-cols-2 gap-2 mb-3">
                          <div className="bg-slate-50 p-2 rounded-xl">
                            <p className="text-xs text-slate-400">Event Date</p>
                            <p className="text-xs font-semibold text-slate-800">{r.date ? new Date(r.date).toLocaleDateString() : '—'}</p>
                            {r.next_occurrence && (
                              <p className="text-xs text-blue-600 font-medium">
                                Next: {r.next_occurrence}
                                {r.owner_hijri_adjustment !== 0 && r.owner_hijri_adjustment != null && r.calendar_type !== 'gregorian' && (
                                  <span className="ml-1 text-[10px] text-emerald-600 font-semibold">(adj {r.owner_hijri_adjustment > 0 ? `+${r.owner_hijri_adjustment}` : r.owner_hijri_adjustment})</span>
                                )}
                              </p>
                            )}
                            {r.hijri_date && <p className="text-xs text-slate-400">Hijri: {r.hijri_date.day}/{r.hijri_date.month}/{r.hijri_date.year}</p>}
                            {r.persian_date && <p className="text-xs text-slate-400">Shamsi: {r.persian_date.day}/{r.persian_date.month}/{r.persian_date.year}</p>}
                          </div>
                          <div className="bg-slate-50 p-2 rounded-xl">
                            <p className="text-xs text-slate-400">Settings</p>
                            <p className="text-xs font-semibold text-slate-800">{r.repeat_yearly ? 'Repeat yearly' : 'One-time'}</p>
                            <p className="text-xs text-slate-500">Remind: {[].concat(r.remind_before_days ?? []).filter(d => d != null).map(d => d === 0 ? 'on day' : `${d}d before`).join(', ') || '—'}</p>
                            {r.group_id && <p className="text-xs text-purple-600">Shared</p>}
                          </div>
                        </div>
                        {/* Notification triggers */}
                        <div className="grid grid-cols-2 gap-2 mb-3">
                          <div className={`p-2 rounded-xl ${
                            r.last_triggered_at ? 'bg-emerald-50' :
                            r.last_trigger_status === 'failed' ? 'bg-red-50' :
                            'bg-slate-50'
                          }`}>
                            <p className="text-xs text-slate-400">Last Triggered</p>
                            {r.last_triggered_at ? (
                              <>
                                <p className="text-xs font-semibold text-emerald-700">{r.last_triggered_at}</p>
                                {r.last_trigger_channel && <p className="text-xs text-slate-500">via {r.last_trigger_channel}</p>}
                              </>
                            ) : r.last_trigger_status === 'failed' ? (
                              <>
                                <p className="text-xs font-semibold text-red-600">Failed</p>
                                <p className="text-xs text-red-400">{r.last_trigger_at_any || '—'}</p>
                                {r.last_trigger_channel && <p className="text-xs text-slate-500">via {r.last_trigger_channel}</p>}
                              </>
                            ) : (
                              <p className="text-xs font-semibold text-slate-500">Never</p>
                            )}
                          </div>
                          <div className={`p-2 rounded-xl ${r.next_scheduled_at ? 'bg-blue-50' : 'bg-slate-50'}`}>
                            <p className="text-xs text-slate-400">Next Scheduled</p>
                            <p className="text-xs font-semibold text-slate-800">{r.next_scheduled_at || 'Not set'}</p>
                            {r.owner_timezone && r.owner_timezone !== 'UTC' && (
                              <p className="text-[10px] text-slate-400">{r.owner_timezone}</p>
                            )}
                          </div>
                        </div>
                        {/* Record dates */}
                        <div className="flex gap-4 text-xs text-slate-400 border-t border-slate-100 pt-2">
                          <span>Created: {r.created_at || '—'}</span>
                          <span>Modified: {r.updated_at || '—'}</span>
                        </div>
                        {/* Force Re-send */}
                        <div className="mt-2 flex items-center gap-2">
                          <button
                            data-testid={`force-resend-btn-${r.id}`}
                            onClick={() => handleForceResend(r.id, r.title)}
                            disabled={forceResendLoading === r.id}
                            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold bg-slate-800 text-white hover:bg-slate-700 disabled:opacity-50 transition-all"
                          >
                            <Zap size={11} className={forceResendLoading === r.id ? 'animate-spin' : ''} />
                            {forceResendLoading === r.id ? 'Queuing…' : 'Force Re-send'}
                          </button>
                          {forceResendResult[r.id] && (
                            <span className={`text-xs font-medium ${forceResendResult[r.id].ok ? 'text-emerald-600' : 'text-red-600'}`}>
                              {forceResendResult[r.id].ok ? '✓ ' : '✗ '}{forceResendResult[r.id].message}
                            </span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* ── Summary (Analytics) ── */}
              {reportTab === 'summary' && (
                <div className="space-y-4">
                  {userAnalytics && (
                    <div className="bg-white p-4 rounded-2xl shadow-[0_4px_20px_rgba(0,0,0,0.04)]">
                      <p className="text-sm font-bold text-slate-900 mb-1 flex items-center gap-2"><Users size={14} />User Growth</p>
                      <p className="text-xs text-slate-400 mb-3">New registrations — last 7 days</p>
                      <MiniBar data={userAnalytics.daily_signups} color="bg-slate-800" />
                      <div className="grid grid-cols-3 gap-2 mt-3">
                        <div className="p-3 bg-blue-50 rounded-xl text-center"><p className="text-lg font-bold text-blue-700">{userAnalytics.total}</p><p className="text-xs text-slate-500">Total</p></div>
                        <div className="p-3 bg-emerald-50 rounded-xl text-center"><p className="text-lg font-bold text-emerald-700">{userAnalytics.new_this_week}</p><p className="text-xs text-slate-500">This week</p></div>
                        <div className="p-3 bg-purple-50 rounded-xl text-center"><p className="text-lg font-bold text-purple-700">{userAnalytics.active_30d}</p><p className="text-xs text-slate-500">Active 30d</p></div>
                      </div>
                      <div className="flex gap-2 mt-2">
                        <div className="flex-1 p-2 bg-slate-50 rounded-xl text-center"><p className="text-sm font-bold text-slate-900">{userAnalytics.by_provider?.email ?? 0}</p><p className="text-xs text-slate-400">Email signup</p></div>
                        <div className="flex-1 p-2 bg-slate-50 rounded-xl text-center"><p className="text-sm font-bold text-slate-900">{userAnalytics.by_provider?.google ?? 0}</p><p className="text-xs text-slate-400">Google signup</p></div>
                      </div>
                    </div>
                  )}
                  {reminderAnalytics && (
                    <div className="bg-white p-4 rounded-2xl shadow-[0_4px_20px_rgba(0,0,0,0.04)]">
                      <p className="text-sm font-bold text-slate-900 mb-1 flex items-center gap-2"><Calendar size={14} />Reminders by Calendar</p>
                      <p className="text-xs text-slate-400 mb-3">Total: {reminderAnalytics.total}</p>
                      <div className="space-y-2">
                        {[
                          { key: 'gregorian', label: 'Gregorian', color: 'bg-blue-500' },
                          { key: 'hijri', label: 'Hijri', color: 'bg-emerald-500' },
                          { key: 'persian', label: 'Persian', color: 'bg-orange-500' },
                        ].map(({ key, label, color }) => {
                          const n = reminderAnalytics.by_calendar?.[key] ?? 0;
                          const p = pct(n, reminderAnalytics.total);
                          return (
                            <div key={key}>
                              <div className="flex justify-between text-xs mb-1"><span className="font-medium text-slate-700">{label}</span><span className="text-slate-500">{n} ({p}%)</span></div>
                              <div className="h-2 bg-slate-100 rounded-full overflow-hidden"><div className={`h-full ${color} rounded-full`} style={{ width: `${p}%` }} /></div>
                            </div>
                          );
                        })}
                      </div>
                      <div className="grid grid-cols-2 gap-2 mt-3">
                        {[
                          { key: 'birthday', label: 'Birthday', color: 'bg-pink-100 text-pink-700' },
                          { key: 'anniversary', label: 'Anniversary', color: 'bg-rose-100 text-rose-700' },
                          { key: 'remember', label: 'Memorial', color: 'bg-slate-100 text-slate-700' },
                          { key: 'other', label: 'Other', color: 'bg-indigo-100 text-indigo-700' },
                        ].map(({ key, label, color }) => (
                          <div key={key} className="p-3 bg-slate-50 rounded-xl">
                            <p className="text-xl font-bold text-slate-900">{reminderAnalytics.by_category?.[key] ?? 0}</p>
                            <p className={`text-xs font-semibold mt-0.5 inline-block px-2 py-0.5 rounded-full ${color}`}>{label}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* ── Notification Queue ── */}
              {reportTab === 'queue' && (
                <div>
                  <p className="text-xs text-slate-400 mb-3">Last 50 notifications</p>
                  {queueReport.length === 0 ? (
                    <p className="text-xs text-slate-400 text-center py-4">No notifications yet</p>
                  ) : (
                    <div className="space-y-2">
                      {queueReport.map((n, i) => (
                        <div key={i} className="p-3 bg-white rounded-xl shadow-[0_2px_8px_rgba(0,0,0,0.04)]" data-testid="queue-report-row">
                          <div className="flex items-start justify-between gap-2 mb-1">
                            <p className="text-xs font-semibold text-slate-900 truncate flex-1">{n.reminder_title}</p>
                            <span className={`text-xs px-2 py-0.5 rounded-full font-semibold shrink-0 ${statusColor[n.status] || 'bg-slate-100 text-slate-600'}`}>{n.status}{n.skip_reason ? ` (${n.skip_reason})` : ''}</span>
                          </div>
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="text-xs text-slate-500 truncate">{n.user_email}</span>
                            <span className={`text-xs px-1.5 py-0.5 rounded-full font-medium ${calColor[n.cal_type] || 'bg-slate-100 text-slate-600'}`}>{n.cal_type}</span>
                            <span className="text-xs text-slate-400">{n.template_key}</span>
                          </div>
                          <div className="flex gap-3 mt-1">
                            {n.scheduled_at && <p className="text-xs text-slate-400">Sched: {new Date(n.scheduled_at).toLocaleDateString()}</p>}
                            {n.sent_at && <p className="text-xs text-slate-400">Sent: {new Date(n.sent_at).toLocaleDateString()}</p>}
                            {n.retry_count > 0 && <p className="text-xs text-amber-600">Retry: {n.retry_count}</p>}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* ── USERS TAB ──────────────────────────── */}
          {tab === 'users' && (
            <div className="space-y-3">
              {/* Search + Filter Panel */}
              <div className="bg-white rounded-2xl p-4 shadow-[0_2px_12px_rgba(0,0,0,0.05)]">
                {/* Email search */}
                <div className="relative mb-3">
                  <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                  <input
                    type="text"
                    value={userSearch}
                    onChange={e => setUserSearch(e.target.value)}
                    onKeyDown={e => e.key === 'Enter' && loadUsers({ search: userSearch, reg_from: userRegFrom, reg_to: userRegTo }, 0)}
                    placeholder="Search by email…"
                    data-testid="user-list-search"
                    className="w-full pl-8 pr-3 py-2 text-sm border border-slate-200 rounded-xl bg-slate-50 focus:outline-none focus:ring-2 focus:ring-slate-300"
                  />
                </div>
                {/* Reg date filters */}
                <div className="mb-3">
                  <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wide mb-1">Registration Date</p>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label className="text-[10px] text-slate-400">From</label>
                      <input type="date" value={userRegFrom} onChange={e => setUserRegFrom(e.target.value)}
                        data-testid="user-list-reg-from"
                        className="w-full mt-0.5 px-2 py-1.5 text-xs border border-slate-200 rounded-xl bg-slate-50 focus:outline-none focus:ring-2 focus:ring-slate-300" />
                    </div>
                    <div>
                      <label className="text-[10px] text-slate-400">To</label>
                      <input type="date" value={userRegTo} onChange={e => setUserRegTo(e.target.value)}
                        data-testid="user-list-reg-to"
                        className="w-full mt-0.5 px-2 py-1.5 text-xs border border-slate-200 rounded-xl bg-slate-50 focus:outline-none focus:ring-2 focus:ring-slate-300" />
                    </div>
                  </div>
                </div>
                <div className="flex gap-2">
                  <button onClick={() => loadUsers({ search: userSearch, reg_from: userRegFrom, reg_to: userRegTo }, 0)}
                    data-testid="user-list-apply-btn" disabled={usersLoading}
                    className="flex-1 py-2 bg-slate-900 text-white text-xs font-semibold rounded-xl hover:bg-slate-700 transition-colors disabled:opacity-50">
                    {usersLoading ? 'Loading…' : 'Apply Filters'}
                  </button>
                  <button onClick={() => { setUserSearch(''); setUserRegFrom(''); setUserRegTo(''); loadUsers({}, 0); }}
                    data-testid="user-list-reset-btn"
                    className="px-4 py-2 border border-slate-200 text-slate-600 text-xs font-semibold rounded-xl hover:bg-slate-50 transition-colors flex items-center gap-1">
                    <X size={12} /> Reset
                  </button>
                </div>
              </div>

              {/* Count + pagination */}
              <div className="flex items-center justify-between">
                <p className="text-xs text-slate-500">
                  {usersTotal > 0
                    ? `Showing ${usersPage * PAGE_SIZE + 1}–${Math.min((usersPage + 1) * PAGE_SIZE, usersTotal)} of ${usersTotal} users`
                    : `${users.length} users`}
                </p>
                {usersTotal > PAGE_SIZE && (
                  <div className="flex items-center gap-2">
                    <button onClick={() => loadUsers({ search: userSearch, reg_from: userRegFrom, reg_to: userRegTo }, usersPage - 1)}
                      disabled={usersPage === 0 || usersLoading}
                      className="p-1.5 border border-slate-200 rounded-xl disabled:opacity-40 hover:bg-slate-50 transition-colors">
                      <ChevronLeft size={14} />
                    </button>
                    <span className="text-xs text-slate-500">{usersPage + 1}/{Math.ceil(usersTotal / PAGE_SIZE)}</span>
                    <button onClick={() => loadUsers({ search: userSearch, reg_from: userRegFrom, reg_to: userRegTo }, usersPage + 1)}
                      disabled={(usersPage + 1) * PAGE_SIZE >= usersTotal || usersLoading}
                      className="p-1.5 border border-slate-200 rounded-xl disabled:opacity-40 hover:bg-slate-50 transition-colors">
                      <ChevronRight size={14} />
                    </button>
                  </div>
                )}
              </div>

              {users.map(u => (
                <div key={u.id} className="bg-white p-4 rounded-2xl shadow-[0_4px_20px_rgba(0,0,0,0.04)]" data-testid="admin-user-row">
                  <div className="flex items-center gap-3 mb-2">
                    <div className="w-10 h-10 bg-slate-200 rounded-full flex items-center justify-center text-sm font-bold text-slate-600">
                      {u.name?.charAt(0)?.toUpperCase() || 'U'}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-slate-900 truncate">{u.name || 'Unknown'}</p>
                      <p className="text-xs text-slate-500 truncate">{u.email}</p>
                    </div>
                    {u.role === 'admin' && <span className="px-2 py-1 bg-amber-100 text-amber-700 rounded-full text-xs font-semibold">Admin</span>}
                  </div>
                  <div className="flex items-center gap-2 mt-2 flex-wrap">
                    <button onClick={() => togglePremium(u.id)} data-testid={`toggle-premium-${u.id}`}
                      className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-all flex items-center gap-1 ${
                        u.subscription?.tier === 'premium' ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-600'
                      }`}>
                      <Crown size={12} />{u.subscription?.tier === 'premium' ? t('admin.premium') : t('admin.free')}
                    </button>
                    {u.role !== 'admin' && (
                      <button onClick={() => toggleActive(u.id)} data-testid={`toggle-active-${u.id}`}
                        className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-all flex items-center gap-1 ${
                          u.active === false ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-700'
                        }`}>
                        <Power size={12} />{u.active === false ? t('admin.deactivated') : t('admin.active')}
                      </button>
                    )}
                    <button onClick={() => toggleAdmin(u.id, u.role)} data-testid={`toggle-admin-${u.id}`}
                      className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-all flex items-center gap-1 ${
                        u.role === 'admin' ? 'bg-amber-100 text-amber-700' : 'bg-slate-100 text-slate-500'
                      }`}>
                      <Crown size={12} />{u.role === 'admin' ? 'Remove Admin' : 'Make Admin'}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* ── CONFIG TAB ─────────────────────────── */}
          {tab === 'config' && (
            <div className="space-y-4">
              {[
                { key: 'persianCalendarEnabled', label: t('admin.persian_cal'), desc: t('admin.persian_cal_desc') },
                { key: 'holidaysEnabled', label: t('admin.public_holidays'), desc: t('admin.public_holidays_desc') },
                { key: 'calendarSyncEnabled', label: t('admin.cal_sync'), desc: t('admin.cal_sync_desc') },
                { key: 'remindBeforeEnabled', label: t('admin.remind_before'), desc: t('admin.remind_before_desc') },
              ].map(item => (
                <div key={item.key} className="bg-white p-4 rounded-2xl shadow-[0_4px_20px_rgba(0,0,0,0.04)] flex items-center justify-between">
                  <div><p className="text-sm font-semibold text-slate-900">{item.label}</p><p className="text-xs text-slate-500">{item.desc}</p></div>
                  <button onClick={() => updateConfig(item.key, !config[item.key])} data-testid={`config-toggle-${item.key}`}
                    className={`w-12 h-7 rounded-full transition-colors relative ${config[item.key] !== false ? 'bg-slate-900' : 'bg-slate-300'}`}>
                    <div className={`w-5 h-5 bg-white rounded-full absolute top-1 transition-transform ${config[item.key] !== false ? 'translate-x-6' : 'translate-x-1'}`} />
                  </button>
                </div>
              ))}
              <div className="bg-white p-4 rounded-2xl shadow-[0_4px_20px_rgba(0,0,0,0.04)]">
                <p className="text-sm font-semibold text-slate-900 mb-2">{t('admin.hijri_adjust')}</p>
                <div className="flex items-center gap-2">
                  {[-2, -1, 0, 1, 2].map(v => (
                    <button key={v} onClick={() => updateConfig('defaultHijriAdjustment', v)}
                      className={`flex-1 py-2 rounded-xl text-sm font-semibold transition-all ${(config.defaultHijriAdjustment || 0) === v ? 'bg-emerald-500 text-white' : 'bg-slate-100 text-slate-600'}`}>
                      {v > 0 ? '+' : ''}{v}
                    </button>
                  ))}
                </div>
              </div>
              <div className="bg-white p-4 rounded-2xl shadow-[0_4px_20px_rgba(0,0,0,0.04)]">
                <p className="text-sm font-semibold text-slate-900 mb-2">{t('admin.max_remind')}</p>
                <input type="number" min="1" max="90" value={config.maxRemindDays || 30}
                  onChange={(e) => updateConfig('maxRemindDays', parseInt(e.target.value) || 30)} data-testid="config-max-remind-days"
                  className="w-full bg-slate-50 border border-transparent focus:border-slate-300 rounded-xl px-4 py-3 text-slate-900" />
              </div>
            </div>
          )}

          {/* ── TRANSLATIONS TAB ───────────────────── */}
          {tab === 'trans' && (
            <div className="space-y-4">
              <div className="bg-white p-4 rounded-2xl shadow-[0_4px_20px_rgba(0,0,0,0.04)]">
                <p className="text-sm font-semibold text-slate-900 mb-3">{t('admin.edit_trans')}</p>
                <div className="flex gap-1 mb-3 overflow-x-auto">
                  {langs.map(l => (
                    <button key={l} onClick={() => setEditLang(l)} data-testid={`trans-lang-${l}`}
                      className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-all shrink-0 ${editLang === l ? 'bg-indigo-500 text-white' : 'bg-slate-100 text-slate-600'}`}>
                      {l.toUpperCase()}
                    </button>
                  ))}
                </div>
                <textarea value={transJsonText} onChange={(e) => setTransJsonText(e.target.value)} rows={10} data-testid="trans-json-editor"
                  className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 text-slate-900 font-mono text-xs resize-none" />
                <button onClick={saveTranslations} disabled={saving} data-testid="trans-save"
                  className="mt-3 w-full bg-slate-900 text-white py-2.5 rounded-full text-sm font-semibold disabled:opacity-50">
                  {saving ? t('admin.saving') : t('admin.save_trans')}
                </button>
              </div>
              <div className="bg-blue-50 p-3 rounded-xl">
                <p className="text-xs text-blue-800">{t('admin.trans_info')}</p>
              </div>
            </div>
          )}

          {/* ── NOTIFICATIONS TEMPLATES TAB ─────────── */}
          {tab === 'notif' && (
            <div className="space-y-4">
              <div className="bg-white p-4 rounded-2xl shadow-[0_4px_20px_rgba(0,0,0,0.04)]">
                <p className="text-sm font-semibold text-slate-900 mb-3">{t('admin.notif_templates')}</p>
                <div className="flex gap-1 mb-3 overflow-x-auto">
                  {langs.map(l => (
                    <button key={l} onClick={() => setEditNotifLang(l)} data-testid={`notif-lang-${l}`}
                      className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-all shrink-0 ${editNotifLang === l ? 'bg-amber-500 text-white' : 'bg-slate-100 text-slate-600'}`}>
                      {l.toUpperCase()}
                    </button>
                  ))}
                </div>
                <textarea value={notifJsonText} onChange={(e) => setNotifJsonText(e.target.value)} rows={8} data-testid="notif-json-editor"
                  className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 text-slate-900 font-mono text-xs resize-none" />
                <button onClick={saveNotifications} disabled={saving} data-testid="notif-save"
                  className="mt-3 w-full bg-slate-900 text-white py-2.5 rounded-full text-sm font-semibold disabled:opacity-50">
                  {saving ? t('admin.saving') : t('admin.save_templates')}
                </button>
              </div>
              <div className="bg-amber-50 p-3 rounded-xl">
                <p className="text-xs text-amber-800">Templates support variables: <code className="bg-amber-100 px-1 rounded">{'{{event}}'}</code> <code className="bg-amber-100 px-1 rounded">{'{{days}}'}</code> <code className="bg-amber-100 px-1 rounded">{'{{month}}'}</code></p>
              </div>
            </div>
          )}

          {/* ── LOGS TAB ───────────────────────────── */}
          {tab === 'logs' && (
            <div className="space-y-2">
              {logs.length === 0 ? (
                <div className="bg-white p-8 rounded-2xl text-center"><p className="text-slate-500 text-sm">{t('admin.no_logs')}</p></div>
              ) : (
                logs.map((log, i) => (
                  <div key={i} className="bg-white p-3 rounded-xl shadow-[0_2px_10px_rgba(0,0,0,0.03)] flex items-start gap-3" data-testid="admin-log-row">
                    <div className={`w-2 h-2 rounded-full mt-1.5 shrink-0 ${
                      log.type === 'feedback' ? 'bg-blue-500' : log.type === 'login_attempt' ? 'bg-red-500' : 'bg-emerald-500'
                    }`} />
                    <div className="flex-1 min-w-0">
                      <p className="text-xs text-slate-900 font-medium truncate">{log.message}</p>
                      <p className="text-xs text-slate-400">{log.user} {log.timestamp ? `- ${new Date(log.timestamp).toLocaleDateString()}` : ''}</p>
                    </div>
                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium shrink-0 ${
                      log.type === 'feedback' ? 'bg-blue-50 text-blue-600' : log.type === 'login_attempt' ? 'bg-red-50 text-red-600' : 'bg-emerald-50 text-emerald-600'
                    }`}>{log.type}</span>
                  </div>
                ))
              )}
            </div>
          )}
        </motion.div>
      </div>
    </div>
  );
};

export default AdminPage;
